import { OperationVariables, TypedDocumentNode } from "@apollo/client";
import { Complete } from "@lyfegen-platform/react-common";
import { Box, Typography } from "@material-ui/core";
import { DocumentNode } from "graphql";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../components/LyfeTable/use-scope-options-styles";
import { useDataQuery } from "../hooks/useDataModule";
import { User } from "../models/generated/graphql";
import { distinctBy } from "../utils/distinct-by";
import LyfeAvatar from "./LyfeAvatar";
import NewCompleteInput from "./NewCompleteInput";

interface SearchAssigneeProps {
	isDisabled: boolean;
	selectedUsers?: Array<UserProps | undefined> | null;
	label?: string | null;
	handleUpdateAssignee: (users: Array<User | undefined>) => void;
	query: DocumentNode | TypedDocumentNode<any, OperationVariables>;
	usersType: string;
	operationVariables?: any;
}

export interface UserProps {
	email: string;
	firstName: string;
	lastName: string;
	tenantId: string;
}

const SearchAssignee: React.FC<SearchAssigneeProps> = ({
	isDisabled,
	operationVariables,
	selectedUsers,
	label,
	handleUpdateAssignee,
	query,
	usersType,
}) => {
	const { t } = useTranslation();
	const { avatar, assigneeSearchComplete, optionsDisabledSearchAssignee } = styles();
	const [selected, setSelected] = useState<Array<Pick<User, "email" | "firstName" | "lastName" | "tenantId"> | undefined>>(
		selectedUsers ?? []
	);
	const { data } = useDataQuery(query, operationVariables);
	const uniqueByUserEmail: any = useMemo(() => {
		if (!data) return;
		return distinctBy(data[usersType] || [], (dist) => (dist as any)?.email || "");
	}, [data]);

	const handleSelect = (val: Array<any>) => {
		const uniqueVal = [...new Set(val.flatMap((elem) => elem))];
		setSelected(uniqueVal);
	};

	const handleSelectionChange = (
		event: React.ChangeEvent<Record<any, unknown>>,
		values: User[],
		reason: "select-option" | "remove-option" | "clear" | "create-option" | "blur"
	) => {
		if (!values.length || isDisabled) {
			return;
		}

		event.preventDefault();
		switch (reason) {
			case "select-option":
			case "remove-option":
				handleSelect(values);
				handleUpdateAssignee(values);
				break;
			case "clear":
				handleSelect([]);
				handleUpdateAssignee(values);
				break;
			default:
				break;
		}
	};

	return (
		<Complete
			id="assigneeSearch"
			disableClearable
			options={[
				...selected,
				...(uniqueByUserEmail ?? []).filter((elem: any) => !selected.map((sel) => sel?.email).includes(elem.email)),
			]}
			classes={{ root: assigneeSearchComplete, popper: isDisabled ? optionsDisabledSearchAssignee : "" }}
			optionLabel="email"
			noOptionsText={t("no_options_available")}
			clearOnBlur={false}
			renderOption={(opt) => (
				<Box display="flex" width="100%" justifyContent="space-between" alignItems="center">
					<Typography>{`${opt.firstName} ${opt.lastName}`}</Typography>
					<LyfeAvatar
						tooltipProps={{
							open: false,
						}}
						size="small"
						userEmail={opt.email}
						firstName={`${opt.firstName.substring(0, 1)}`}
						lastName={`${opt.lastName.substring(0, 1)}`}
						avatarProps={{ className: avatar }}
					/>
				</Box>
			)}
			multiple
			value={selected}
			getOptionLabel={(optLabel) => `${optLabel.firstName} ${optLabel.lastName}`}
			getOptionSelected={(firstObj: any, secObj: any) => firstObj === secObj || firstObj["email"] === secObj["email"]}
			onChange={handleSelectionChange}
			renderInput={(params) => {
				return (
					<NewCompleteInput
						label={label || ""}
						params={{
							...params,
							InputProps: {
								...params.InputProps,
								startAdornment: (params.InputProps.startAdornment as Array<any> | undefined)?.map((elem, i) => ({
									...elem,
									props:
										selected.length > 1
											? { ...elem.props, label: `${selected[i]!.firstName} ${selected[i]!.lastName}` }
											: { label: `${selected[i]!.firstName} ${selected[i]!.lastName}` },
								})),
							},
							inputProps: {
								...params.inputProps,
								style: { maxWidth: "50px", marginTop: "-2px" },
							},
						}}
						noChips
					/>
				);
			}}
		/>
	);
};

export default SearchAssignee;
